import React, { useState, useEffect, useRef } from 'react';
import { Building, ChevronDown, X } from 'lucide-react';
import api from 'services/api';

const CompanyAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Selecione uma empresa",
  error = null,
  disabled = false,
  inputId,
  inputName = 'company_autocomplete',
  ariaDescribedBy,
  ariaInvalid = false,
  showErrorMessage = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allCompanies, setAllCompanies] = useState([]); // Todas as empresas do usuário
  const [filteredCompanies, setFilteredCompanies] = useState([]); // Empresas filtradas
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Carregar empresas do usuário uma única vez
  useEffect(() => {
    const loadUserCompanies = async () => {
      setLoading(true);
      try {
        // Buscar dados do usuário atual que já incluem as empresas acessíveis
        const response = await api.get('/users/me');
        const userCompanies = response.data.accessible_companies || [];
        setAllCompanies(userCompanies);
      } catch (error) {
        console.error('Erro ao buscar empresas do usuário:', error);
        setAllCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserCompanies();
  }, []);

  // Filtrar empresas baseado no termo de busca
  useEffect(() => {
    if (searchTerm.length < 2) {
      setFilteredCompanies([]);
      return;
    }

    const filtered = allCompanies.filter(company => 
      company.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredCompanies(filtered);
  }, [searchTerm, allCompanies]);

  // Carregar empresa selecionada quando value muda
  useEffect(() => {
    if (value && typeof value === 'object') {
      setSelectedCompany(value);
      setSearchTerm(value.name || '');
    } else if (value && typeof value === 'string') {
      setSearchTerm(value);
    } else {
      setSelectedCompany(null);
      setSearchTerm('');
    }
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    
    // Se o usuário limpar o campo, limpar a seleção
    if (!newValue) {
      setSelectedCompany(null);
      onChange(null);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setSearchTerm(company.name);
    setIsOpen(false);
    onChange(company);
  };

  const handleClear = () => {
    setSelectedCompany(null);
    setSearchTerm('');
    setIsOpen(false);
    onChange(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted h-4 w-4" />
        <input
          id={inputId}
          name={inputName}
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          autoComplete="organization"
          className={`w-full pl-10 pr-20 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface ${
            error ? 'border-red-300' : ''
          } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
          style={{
            borderColor: error ? undefined : 'var(--border)',
            color: 'var(--text-primary)',
            background: disabled ? 'var(--surface-muted)' : 'var(--surface)'
          }}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {selectedCompany && (
            <button
              type="button"
              onClick={handleClear}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              disabled={disabled}
              aria-label="Limpar empresa selecionada"
            >
              <X className="h-4 w-4 theme-text-muted" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={isOpen ? 'Fechar lista de empresas' : 'Abrir lista de empresas'}
          >
            <ChevronDown className={`h-4 w-4 theme-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto theme-surface"
          style={{ borderColor: 'var(--border)' }}
        >
          {loading ? (
            <div className="p-3 text-center theme-text-muted">
              <div className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-2">Buscando...</span>
            </div>
          ) : filteredCompanies.length > 0 ? (
            filteredCompanies.map((company) => (
              <button
                key={company.id}
                type="button"
                onClick={() => handleCompanySelect(company)}
                className="w-full px-4 py-3 text-left focus:outline-none transition-colors hover:opacity-90"
              >
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: company.primary_color }}
                  ></div>
                  <div>
                    <div className="font-medium theme-text">{company.name}</div>
                    <div className="text-sm theme-text-muted">{company.slug}</div>
                  </div>
                </div>
              </button>
            ))
          ) : searchTerm.length >= 2 ? (
            <div className="p-3 text-center theme-text-muted">
              Nenhuma empresa encontrada para "{searchTerm}"
            </div>
          ) : (
            <div className="p-3 text-center theme-text-muted">
              Digite pelo menos 2 caracteres para buscar
            </div>
          )}
        </div>
      )}

      {error && showErrorMessage && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CompanyAutocomplete;
